import { Fragment } from 'react'

import { fontFor } from '../format'
import type { CmpBlock } from '../spec/types'

/**
 * Two records, field by field, with the verdict spelled out underneath.
 *
 * Verbatim from `docs/design/reference/blocks/cmp.html`. Two panels share a
 * 1px hairline grid so the fields line up on the same baselines, each panel
 * carries an edge stripe for its own tone, and a footer bar states which of
 * the two figures is the answer.
 *
 * This is the block invariant 2 is argued on. Our ledger is the operational
 * system of record and FinXP's record is legally authoritative, and when they
 * disagree an operator has to see BOTH — unreconciled, unmerged, still
 * disagreeing — before deciding. A single merged view would have already made
 * the decision for them. The verdict line then says, in words, which figure
 * the customer is seeing, because the resolution is a new posting (never an
 * edit) and the screen must not imply otherwise.
 *
 * Values arrive as the raw record fields, strings as they stand in each store
 * — `amount_minor` shows `8735`, not `87.35 EUR`. That is deliberate and is
 * not a missed call to `formatMinor`: the point of the panel is to show each
 * record exactly as it is held, and normalising it here would smooth away the
 * very difference being adjudicated. Formatted amounts belong in the verdict
 * sentence, where the corpus puts them.
 */
export function Cmp({ block }: { block: CmpBlock }) {
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1px',
          background: 'var(--k-line)',
          border: '1px solid var(--k-line)',
        }}
      >
        {block.sides.map((side) => (
          <div
            key={side.label}
            data-edge={side.edge ?? 'n'}
            style={{ background: 'var(--k-panel)', padding: '11px 12px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 800,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                }}
              >
                {side.label}
              </span>
              {/* Free text, not the `sourceBadge` vocabulary — a side names its
                  own provenance ("Provider · legally authoritative"), which is
                  finer-grained than the block-level source badge. */}
              <span
                data-tone={side.tone ?? 'n'}
                style={{
                  padding: '0 5px',
                  fontSize: '9.5px',
                  fontWeight: 800,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                }}
              >
                {side.src}
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: '10.5px',
                  color: 'var(--k-mut2)',
                  fontFamily: 'var(--k-mono)',
                }}
              >
                {side.meta ?? ''}
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '4px 12px',
                marginTop: '9px',
                fontSize: '12px',
              }}
            >
              {side.items.map((item) => (
                // Each item spans two grid tracks, so the pair is emitted flat
                // rather than boxed — a wrapper would break the column
                // alignment the comparison depends on.
                <Fragment key={item[0]}>
                  <span
                    style={{
                      color: 'var(--k-mut2)',
                      fontSize: '11px',
                      fontFamily: 'var(--k-mono)',
                    }}
                  >
                    {item[0]}
                  </span>
                  {/* A tone is only set on the fields that carry the argument,
                      and those render at 700 — so the eye lands on where the
                      two records part, not on the fields where they agree. */}
                  <span
                    data-tone={item[2]}
                    style={{
                      fontFamily: fontFor(item[3]),
                      padding: '0 3px',
                      fontWeight: item[2] !== '' ? 700 : 500,
                    }}
                  >
                    {item[1]}
                  </span>
                </Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 12px',
          border: '1px solid var(--k-line)',
          borderTop: 0,
          background: 'var(--k-bg)',
          fontSize: '11.5px',
        }}
      >
        <span style={{ fontWeight: 600 }}>{block.verdict}</span>
        <span style={{ color: 'var(--k-mut)' }}>{block.note2 ?? ''}</span>
      </div>
    </div>
  )
}
